import React from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Paper,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
} from "@mui/material";
import {
  Air as RespirationIcon,
  Opacity as OxygenIcon,
  MonitorHeart as BloodPressureIcon,
  Favorite as HeartRateIcon,
  Thermostat as TemperatureIcon,
  Psychology as ConsciousnessIcon,
  InfoOutlined as InfoIcon,
  Edit as EditIcon,
  MonitorHeart,
} from "@mui/icons-material";
import CustomInput from "../../../../../FormFields/CustomInput/CustomInput";
import { consciousnessLevels } from "./news2.config";
import { useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { createNEWS2 } from "../../../../../../api/services/observations";
import CustomDateTime from "../../../../../CustomForm/CustomDateTime";
import ClientNews2List from "./ClientNews2List";
import { QK } from "../../../../../../base/qk";

const NEWS2 = () => {
  const { id: clientID } = useParams();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    setError,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      air_type: "Air",
      client: clientID,
      actioned_time: new Date(),
    },
  });

  const watchedValues = watch();

  const handleAirTypeChange = (type) => {
    setValue("air_type", type);
  };

  const handleConsciousnessChange = (level) => {
    setValue("consciousness_level", level);
  };

  const onSubmit = async (data) => {
    try {
      const response = await createNEWS2(data);
      if (response.status === 201) {
        reset({
          air_type: "Air",
          client: clientID,
        });
        enqueueSnackbar("NEWS2 data added", { variant: "success" });
        queryClient.invalidateQueries([QK.NEWS2lists]);
      }
    } catch (error) {
      if (error.status === 400 && error.message) {
        const validationErrors = error.message;

        Object.keys(validationErrors).forEach((fieldName) => {
          const errorMessages = validationErrors[fieldName];
          if (Array.isArray(errorMessages) && errorMessages.length > 0) {
            setError(fieldName, {
              type: "server",
              message: errorMessages[0],
            });
          }
        });

        enqueueSnackbar("Please fix the validation errors", {
          variant: "error",
        });
      } else {
        enqueueSnackbar("Failed to add NEWS2 data", { variant: "error" });
      }
    }
  };

  const handleCancel = () => {
    reset({
      air_type: "Air",
      client: clientID,
    });
  };

  return (
    <div>
      <Paper
        elevation={2}
        sx={{ p: 3, maxWidth: 700, minWidth: 600, mx: "auto" }}
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "grey.700", width: 40, height: 40 }}>
                <Tooltip title="Respiration Rate - Breaths per minute">
                  <RespirationIcon sx={{ fontSize: 20 }} />
                </Tooltip>
              </Avatar>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}
              >
                <Box sx={{ flex: 1 }}>
                  <Controller
                    name="respiration_rate"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        {...field}
                        label="Respiration Rate"
                        type="number"
                        error={!!errors.respiration_rate}
                        helperText={errors.respiration_rate?.message}
                      />
                    )}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  rpm
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "grey.700", width: 40, height: 40 }}>
                <Tooltip title="Oxygen Saturation - Percentage of oxygen in blood">
                  <OxygenIcon sx={{ fontSize: 20 }} />
                </Tooltip>
              </Avatar>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}
              >
                <Box sx={{ flex: 1 }}>
                  <Controller
                    name="oxygen_saturation"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        {...field}
                        label="Oxygen Saturation"
                        type="number"
                        error={!!errors.oxygen_saturation}
                        helperText={errors.oxygen_saturation?.message}
                      />
                    )}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  % Scale 100
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1, ml: 6.5 }}>
              <Button
                variant={
                  watchedValues.air_type === "Air" ? "contained" : "outlined"
                }
                onClick={() => handleAirTypeChange("Air")}
                size="small"
                sx={{
                  bgcolor:
                    watchedValues.air_type === "Air"
                      ? "#8BC34A"
                      : "transparent",
                  color: watchedValues.air_type === "Air" ? "black" : "#8BC34A",
                  borderColor: "#8BC34A",
                  "&:hover": {
                    bgcolor:
                      watchedValues.air_type === "Air"
                        ? "#7CB342"
                        : "rgba(139, 195, 74, 0.1)",
                  },
                }}
              >
                Air
              </Button>
              <Button
                variant={
                  watchedValues.air_type === "Oxygen" ? "contained" : "outlined"
                }
                onClick={() => handleAirTypeChange("Oxygen")}
                size="small"
                sx={{
                  bgcolor:
                    watchedValues.air_type === "Oxygen"
                      ? "#2196F3"
                      : "transparent",
                  color:
                    watchedValues.air_type === "Oxygen" ? "white" : "#2196F3",
                  borderColor: "#2196F3",
                  "&:hover": {
                    bgcolor:
                      watchedValues.air_type === "Oxygen"
                        ? "#1976D2"
                        : "rgba(33, 150, 243, 0.1)",
                  },
                }}
              >
                Oxygen
              </Button>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "grey.700", width: 40, height: 40 }}>
                <Tooltip title="Blood Pressure - Systolic/Diastolic pressure">
                  <MonitorHeart sx={{ fontSize: 20 }} />
                </Tooltip>
              </Avatar>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Controller
                      name="systolic_bp"
                      control={control}
                      render={({ field }) => (
                        <CustomInput
                          {...field}
                          label="Systolic"
                          type="number"
                          error={!!errors.systolic_bp}
                          helperText={errors.systolic_bp?.message}
                        />
                      )}
                    />
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    /
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <Controller
                      name="diastolic_bp"
                      control={control}
                      render={({ field }) => (
                        <CustomInput
                          {...field}
                          label="Diastolic"
                          type="number"
                          error={!!errors.diastolic_bp}
                          helperText={errors.diastolic_bp?.message}
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  mmHg
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "grey.700", width: 40, height: 40 }}>
                <Tooltip title="Heart Rate - Beats per minute">
                  <HeartRateIcon sx={{ fontSize: 20 }} />
                </Tooltip>
              </Avatar>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}
              >
                <Box sx={{ flex: 1 }}>
                  <Controller
                    name="heart_rate"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        {...field}
                        label="Heart Rate"
                        type="number"
                        error={!!errors.heart_rate}
                        helperText={errors.heart_rate?.message}
                      />
                    )}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  bpm
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "grey.700", width: 40, height: 40 }}>
                <Tooltip title="Level of Consciousness - AVPU Scale">
                  <ConsciousnessIcon sx={{ fontSize: 20 }} />
                </Tooltip>
              </Avatar>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flex: 1,
                }}
              >
                {consciousnessLevels.map((level) => (
                  <Tooltip key={level.value} title={level.tooltip}>
                    <Button
                      variant={
                        watchedValues.consciousness_level === level.value
                          ? "contained"
                          : "outlined"
                      }
                      onClick={() => handleConsciousnessChange(level.value)}
                      size="small"
                      sx={{
                        minWidth: 32,
                        width: 32,
                        height: 32,
                        fontSize: "12px",
                        fontWeight: "bold",
                        bgcolor:
                          watchedValues.consciousness_level === level.value
                            ? level.color
                            : "transparent",
                        color:
                          watchedValues.consciousness_level === level.value
                            ? "white"
                            : level.color,
                        borderColor: level.color,
                        "&:hover": {
                          bgcolor:
                            watchedValues.consciousness_level === level.value
                              ? level.color
                              : `${level.color}20`,
                        },
                      }}
                    >
                      {level.value}
                    </Button>
                  </Tooltip>
                ))}
                <Tooltip title="Information about consciousness levels">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon color="primary" />
                  </IconButton>
                </Tooltip>
              </Box>
              {errors.consciousness_level && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {errors.consciousness_level.message}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "grey.700", width: 40, height: 40 }}>
                <Tooltip title="Body Temperature - Degrees Celsius">
                  <TemperatureIcon sx={{ fontSize: 20 }} />
                </Tooltip>
              </Avatar>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}
              >
                <Box sx={{ flex: 1 }}>
                  <Controller
                    name="temperature"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        {...field}
                        label="Temperature"
                        type="decimal"
                        step="0.1"
                        error={!!errors.temperature}
                        helperText={errors.temperature?.message}
                      />
                    )}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  °C
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <CustomInput
                    {...field}
                    label="Record notes/action taken (including O2 flow rate [L/min] and how delivered)"
                    multiline={true}
                    rows={4}
                    error={!!errors.notes}
                    helperText={errors.notes?.message}
                  />
                )}
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <Controller
                name="actioned_time"
                control={control}
                render={({ field }) => (
                  <CustomDateTime
                    {...field}
                    label="Actioned Time"
                    error={!!errors.notes}
                    helperText={errors.notes?.message}
                  />
                )}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{ flex: 1 }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" sx={{ flex: 1 }}>
                Save
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
      <div className="mt-3 shadow w-75 mx-auto">
        <ClientNews2List></ClientNews2List>
      </div>
    </div>
  );
};

export default NEWS2;